(function initializeVoyadoSession(window, document) {
    const context = window.VOYADO_CONTEXT;
    if (!context?.enabled || !context.clusterId) {
        return;
    }

    const INITIALIZATION_TIMEOUT = 5000;
    const POLL_INTERVAL = 100;
    const USER_CONTEXT = Object.freeze({ anonymous: 'ANONYMOUS' });
    const EVENTS = Object.freeze({
        shopifyConsentChanged: 'visitorConsentCollected',
        voyadoConsentChanged: 'voyado:consent-updated',
    });
    const CONSENT_TRACKING_FEATURE = Object.freeze({
        name: 'consent-tracking-api',
        version: '0.1',
    });
    // These keys belonged to the retired theme-owned session. They are only
    // removed; the theme no longer creates or reads Elevate identifiers.
    const LEGACY_STORAGE_KEYS = Object.freeze([
        'voyado_elevate_authenticated_customer_key',
        'voyado_elevate_customer_key',
        'voyado_elevate_session_key',
    ]);

    let resolveReady;
    let previousRequestContext = null;
    let pendingSessionEndContext = null;
    let sessionResolutionPromise = null;
    const ready = new Promise((resolve) => {
        resolveReady = resolve;
    });

    function hasConsent() {
        return window.Shopify?.customerPrivacy
            ?.analyticsProcessingAllowed?.() === true;
    }

    function anonymousContext() {
        return { userContext: USER_CONTEXT.anonymous };
    }

    function getOfficialSessionResolver(elevate) {
        // Voyado's documented SDK lets the app provide a `session` callback,
        // but does not document a getter on window.elevate. The observed app
        // runtime shape exposes that callback on query/notify configuration;
        // this private fallback must be rechecked when the app changes.
        for (const client of [elevate?.query, elevate?.notify]) {
            const configuration = client?.c;
            if (typeof configuration?.session === 'function') {
                return {
                    resolve: configuration.session,
                    receiver: configuration,
                };
            }
        }

        return null;
    }

    async function resolveOfficialSession(deadline) {
        let pollTimer = null;
        let timeoutTimer = null;

        try {
            const resolver = await Promise.race([
                new Promise((resolve, reject) => {
                    const findResolver = () => {
                        let available;
                        try {
                            available = getOfficialSessionResolver(window.elevate);
                        } catch (error) {
                            reject(error);
                            return;
                        }
                        if (available) {
                            resolve(available);
                            return;
                        }

                        const remaining = deadline - Date.now();
                        if (remaining <= 0) {
                            reject(new Error('Voyado Elevate session is unavailable.'));
                            return;
                        }

                        pollTimer = window.setTimeout(
                            findResolver,
                            Math.min(POLL_INTERVAL, remaining)
                        );
                    };

                    findResolver();
                }),
                new Promise((_, reject) => {
                    timeoutTimer = window.setTimeout(
                        () => reject(new Error('Voyado Elevate session resolution timed out.')),
                        Math.max(0, deadline - Date.now())
                    );
                }),
            ]);
            window.clearTimeout(timeoutTimer);
            timeoutTimer = null;

            const remaining = deadline - Date.now();
            if (remaining <= 0) {
                throw new Error('Voyado Elevate session resolution timed out.');
            }

            return await Promise.race([
                Promise.resolve().then(() => resolver.resolve.call(resolver.receiver)),
                new Promise((_, reject) => {
                    timeoutTimer = window.setTimeout(
                        () => reject(new Error('Voyado Elevate session resolution timed out.')),
                        remaining
                    );
                }),
            ]);
        } finally {
            window.clearTimeout(pollTimer);
            window.clearTimeout(timeoutTimer);
        }
    }

    function normalizeOfficialSession(session) {
        const userContext = String(session?.userContext ?? '').trim().toUpperCase();
        if (userContext === USER_CONTEXT.anonymous) {
            return anonymousContext();
        }

        const customerKey = String(session?.customerKey ?? '').trim();
        const sessionKey = String(session?.sessionKey ?? '').trim();

        if (userContext === 'UNPERSONALIZED') {
            return {
                userContext,
                ...(sessionKey ? { sessionKey } : {}),
            };
        }

        if (customerKey && sessionKey) {
            return {
                customerKey,
                sessionKey,
                ...(userContext ? { userContext } : {}),
            };
        }

        if (userContext === 'SYNTHETIC') {
            return {
                userContext,
                ...(sessionKey ? { sessionKey } : {}),
            };
        }

        return anonymousContext();
    }

    async function resolveRequestContext() {
        const deadline = Date.now() + INITIALIZATION_TIMEOUT;
        await ready;
        if (!hasConsent()) {
            return anonymousContext();
        }

        if (!sessionResolutionPromise) {
            sessionResolutionPromise = resolveOfficialSession(deadline)
                .then(normalizeOfficialSession)
                .catch((error) => {
                    if (context.debug === true) {
                        console.info('[Voyado session] Official session unavailable; continuing without identifiers.', error);
                    }
                    return anonymousContext();
                })
                .finally(() => {
                    sessionResolutionPromise = null;
                });
        }

        return sessionResolutionPromise.then((requestContext) => {
            if (!hasConsent()) {
                return anonymousContext();
            }

            // Use the same value synced to Engage's discoveryKey. Only replace
            // an existing personalized identity; preserve the app's privacy mode.
            const customerKey = String(context.customerKey ?? '').trim();
            if (
                customerKey &&
                requestContext.customerKey &&
                requestContext.sessionKey &&
                !['ANONYMOUS', 'UNPERSONALIZED', 'SYNTHETIC'].includes(requestContext.userContext)
            ) {
                requestContext = {
                    ...requestContext,
                    customerKey,
                };
            }

            if (requestContext.customerKey && requestContext.sessionKey) {
                previousRequestContext = requestContext;
            }
            return requestContext;
        });
    }

    function getStorage(storageName) {
        try {
            return window[storageName];
        } catch {
            return null;
        }
    }

    function clearLegacyStorage() {
        for (const storageName of ['localStorage', 'sessionStorage']) {
            const storage = getStorage(storageName);
            if (!storage) continue;

            for (const key of LEGACY_STORAGE_KEYS) {
                try {
                    storage.removeItem(key);
                } catch {
                    // Storage can be disabled; it is never read by this module.
                }
            }
        }
    }

    function dispatchConsentChangedEvent(consentGranted, priorContext) {
        window.dispatchEvent(new CustomEvent(EVENTS.voyadoConsentChanged, {
            detail: {
                hasConsent: consentGranted,
                requestContext: consentGranted
                    ? null
                    : anonymousContext(),
                previousRequestContext: priorContext,
            },
        }));
    }

    function handleConsentChange() {
        const consentGranted = hasConsent();
        const priorContext = consentGranted ? null : previousRequestContext;

        if (!consentGranted) {
            pendingSessionEndContext = priorContext;
            previousRequestContext = null;
            clearLegacyStorage();
        }

        dispatchConsentChangedEvent(consentGranted, priorContext);
    }

    function exposePublicApi() {
        window.VOYADO_SESSION = Object.freeze({
            ready,
            hasConsent,
            getRequestContext: resolveRequestContext,
            getCustomerKey: async () => (await resolveRequestContext()).customerKey ?? null,
            getSessionKey: async () => (await resolveRequestContext()).sessionKey ?? null,
            getUserContext: async () => (await resolveRequestContext()).userContext ?? null,
            consumePendingSessionEndContext() {
                const requestContext = pendingSessionEndContext;
                pendingSessionEndContext = null;
                return requestContext;
            },
            clearStorage: clearLegacyStorage,
        });
    }

    function loadShopifyCustomerPrivacyApi() {
        if (typeof window.Shopify?.loadFeatures !== 'function') {
            resolveReady();
            return;
        }

        let completed = false;
        let privacyTimeout = null;
        const finish = (error) => {
            if (completed) return;
            completed = true;
            window.clearTimeout(privacyTimeout);
            if (error && context.debug === true) {
                console.info('[Voyado session] Shopify consent API could not be loaded.', error);
            }
            resolveReady();
        };
        privacyTimeout = window.setTimeout(
            () => finish(new Error('Shopify consent API loading timed out.')),
            INITIALIZATION_TIMEOUT
        );

        try {
            window.Shopify.loadFeatures([CONSENT_TRACKING_FEATURE], finish);
        } catch (error) {
            finish(error);
        }
    }

    clearLegacyStorage();
    exposePublicApi();
    document.addEventListener(EVENTS.shopifyConsentChanged, handleConsentChange);
    loadShopifyCustomerPrivacyApi();
})(window, document);
